import { Link } from '@tanstack/react-router'
import { motion, useMotionValueEvent, useScroll } from 'framer-motion'
import { useTranslations } from 'gt-react'
import { User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { getCurrentUser } from '@/lib/api'
import { cn } from '@/lib/utils'
import { LanguageSwitcher } from './language-switcher'
import { ThemeToggle } from './theme-toggle'

/**
 * Sticky top-of-page navigation. Transparent at the top of the page,
 * adds a backdrop-blur + border when the user scrolls past 32px so
 * content underneath stays readable.
 */
export function NavBar() {
  const t = useTranslations('nav')
  const [scrolled, setScrolled] = useState(false)
  const [isAuthed, setIsAuthed] = useState(false)
  const { scrollY } = useScroll()

  useEffect(() => {
    const ctrl = new AbortController()
    getCurrentUser(ctrl.signal)
      .then(() => setIsAuthed(true))
      .catch(() => setIsAuthed(false))
    return () => ctrl.abort()
  }, [])

  useMotionValueEvent(scrollY, 'change', (y) => {
    setScrolled(y > 32)
  })

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-200',
        scrolled
          ? 'bg-[#0a0a0f]/80 backdrop-blur-md border-b border-white/[0.06]'
          : 'bg-transparent',
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        {/* Brand mark */}
        <Link to="/" className="flex items-center gap-2 group">
          <BrandMark />
          <span className="font-semibold text-base tracking-tight text-white group-hover:text-white/80 transition-colors">
            Beyond
          </span>
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink href="/#features">{t('features')}</NavLink>
          <NavLink href="/pricing">{t('pricing')}</NavLink>
          <NavLink href="/#docs">{t('docs')}</NavLink>
          <Link
            to="/api/docs"
            className="text-sm font-medium text-white/40 hover:text-white/80 transition-colors"
          >
            {t('developers')}
          </Link>
        </nav>

        {/* Right cluster: language, theme, auth */}
        <div className="flex items-center gap-2">
          <LanguageSwitcher className="hidden sm:inline-flex" />
          <ThemeToggle />
          <div className="hidden sm:flex items-center gap-2 pl-2 ml-1 border-l border-white/10">
            {isAuthed ? (
              <Link
                to="/account"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium',
                  'bg-white text-[#0a0a0f]',
                  'hover:bg-white/90 transition-all',
                )}
              >
                <User className="h-3.5 w-3.5" />
                {t('account') || 'Account'}
              </Link>
            ) : (
              <>
                <Link
                  to="/sign-in"
                  className="text-sm font-medium text-white/40 hover:text-white/80 transition-colors px-3 py-1.5"
                >
                  {t('sign_in')}
                </Link>
                <Link
                  to="/sign-up"
                  className={cn(
                    'inline-flex items-center rounded-lg px-4 py-1.5 text-sm font-medium',
                    'bg-white text-[#0a0a0f]',
                    'hover:bg-white/90 transition-all',
                  )}
                >
                  {t('get_started')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  )
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="text-sm font-medium text-white/40 hover:text-white/80 transition-colors"
    >
      {children}
    </a>
  )
}

/**
 * Brand mark — a compact animated gradient tile with a "c" glyph.
 * Inline SVG so it's one DOM node, no network request.
 */
function BrandMark() {
  return (
    <div className="relative h-8 w-8 rounded-lg overflow-hidden shadow-sm">
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
        }}
      />
      <svg
        viewBox="0 0 32 32"
        fill="none"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <path d="M22 11a8 8 0 1 0 0 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </div>
  )
}
